import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Switch } from '@orisini/ui/components/switch'
import { cn } from '@orisini/ui/lib/utils'
import { brandApi, type DurationMode, type Formats, type Style, type StyleMode, type StylePick } from '../../brand/lib/brand'
import type { Workspace } from '../lib/accounts'

const MODES: { value: StyleMode; label: string; hint: string }[] = [
  { value: 'random', label: 'Random', hint: 'A different style each time, picked at random.' },
  { value: 'sequential', label: 'Sequential', hint: 'Take turns through the styles in order.' },
  { value: 'fixed', label: 'Always one style', hint: 'Every post uses the same style.' },
]

const DURATIONS: { value: DurationMode; label: string }[] = [
  { value: 'auto', label: 'Auto' },
  { value: '1-5', label: '1–5 mins' },
  { value: '5-10', label: '5–10 mins' },
  { value: 'custom', label: 'Custom' },
]

/**
 * Content Formats: jenis konten yang diproduksi akun ini dan cara memilih
 * style-nya. Dipakai produksi (fase 4) dan video (fase 5).
 */
export function FormatsTab({ workspace: w }: { workspace: Workspace }) {
  const [formats, setFormats] = useState<Formats | null>(null)
  const [saved, setSaved] = useState<string>('')
  const [styles, setStyles] = useState<Omit<Style, 'path'>[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    brandApi
      .formats(w.id)
      .then((r) => {
        setFormats(r.formats)
        setSaved(JSON.stringify(r.formats))
        setStyles(r.styles)
      })
      .catch((err) => toast.error(err instanceof Error ? err.message : "Couldn't load content formats"))
  }, [w.id])

  if (!formats) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Loading…
      </div>
    )
  }

  const simpan = async () => {
    setSaving(true)
    try {
      const r = await brandApi.saveFormats(w.id, formats)
      setFormats(r.formats)
      setSaved(JSON.stringify(r.formats))
      toast.success('Content formats saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save content formats")
    } finally {
      setSaving(false)
    }
  }

  const dirty = JSON.stringify(formats) !== saved
  const menit = formats.video.duration.minutes

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        What this project produces from the stories you pick. Styles come from{' '}
        <Link to={`/projects/${w.id}/brand`} className="text-foreground underline-offset-4 hover:underline">Brand &amp; Styles</Link>.
      </p>

      <Card className="flex items-center gap-4 p-4">
        <div className="min-w-0 flex-1">
          <p className="font-medium">Poster</p>
          <p className="text-sm text-muted-foreground">One image.</p>
        </div>
        <Switch checked={formats.poster.enabled} onCheckedChange={(v) => setFormats({ ...formats, poster: { enabled: v } })} aria-label="Produce posters" />
      </Card>

      <PickCard
        title="Carousel"
        hint="Several slides."
        pick={formats.carousel}
        styles={styles.filter((s) => s.kind === 'carousel' && s.active)}
        onChange={(carousel) => setFormats({ ...formats, carousel })}
      />

      <PickCard
        title="Video"
        hint="Vertical video with a generated or uploaded voice-over, 9:16."
        pick={formats.video}
        styles={styles.filter((s) => s.kind === 'video' && s.active)}
        onChange={(v) => setFormats({ ...formats, video: { ...formats.video, ...v } })}
      >
        <div className="space-y-2">
          <p className="text-sm font-medium">Length</p>
          <div className="flex flex-wrap items-center gap-2">
            {DURATIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                aria-pressed={formats.video.duration.mode === d.value}
                onClick={() => setFormats({ ...formats, video: { ...formats.video, duration: { mode: d.value, minutes: d.value === 'custom' ? (menit ?? 3) : null } } })}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-sm',
                  formats.video.duration.mode === d.value ? 'border-primary bg-primary/10' : 'border-border text-muted-foreground',
                )}
              >
                {d.label}
              </button>
            ))}
            {formats.video.duration.mode === 'custom' && (
              <span className="flex items-center gap-2 text-sm">
                <Input
                  type="number"
                  min={0.5}
                  max={60}
                  step={0.5}
                  className="h-9 w-24"
                  value={menit ?? ''}
                  onChange={(e) => setFormats({ ...formats, video: { ...formats.video, duration: { mode: 'custom', minutes: e.target.value ? Number(e.target.value) : null } } })}
                  aria-label="Minutes"
                />
                minutes
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {formats.video.duration.mode === 'auto'
              ? 'Orisini picks a length from the story and keeps it within what the platform accepts.'
              : formats.video.duration.mode === 'custom' && menit && menit > 10
                ? "Videos longer than 10 minutes are rendered on your computer, so Orisini Agent has to be running. They don't use video minutes."
                : 'The final length follows the voice-over, aimed at this range.'}
          </p>
        </div>
      </PickCard>

      <div className="flex items-center gap-2">
        <Button onClick={simpan} disabled={!dirty || saving}>
          {saving && <Loader2 className="animate-spin" />}
          Save formats
        </Button>
        {dirty && (
          <Button variant="ghost" disabled={saving} onClick={() => setFormats(JSON.parse(saved) as Formats)}>
            Discard changes
          </Button>
        )}
      </div>
    </div>
  )
}

function PickCard({
  title,
  hint,
  pick,
  styles,
  onChange,
  children,
}: {
  title: string
  hint: string
  pick: StylePick
  styles: Omit<Style, 'path'>[]
  onChange: (p: StylePick) => void
  children?: React.ReactNode
}) {
  const semua = pick.styles.length === 0
  const dipakai = (slug: string) => semua || pick.styles.includes(slug)
  const toggleStyle = (slug: string) => {
    const sekarang = semua ? styles.map((s) => s.slug) : pick.styles
    const next = sekarang.includes(slug) ? sekarang.filter((x) => x !== slug) : [...sekarang, slug]
    // Semua dicentang = simpan sebagai "semua", supaya style baru ikut otomatis.
    onChange({ ...pick, styles: next.length === styles.length ? [] : next })
  }

  return (
    <Card className="space-y-4 p-4">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{hint}</p>
        </div>
        <Switch checked={pick.enabled} onCheckedChange={(v) => onChange({ ...pick, enabled: v })} aria-label={`Produce ${title.toLowerCase()}s`} />
      </div>

      {pick.enabled && (
        <>
          {styles.length === 0 ? (
            <p className="text-sm text-muted-foreground">No {title.toLowerCase()} styles yet. Create one under Brand &amp; Styles.</p>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium">Style selection</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {MODES.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      aria-pressed={pick.mode === m.value}
                      onClick={() => onChange({ ...pick, mode: m.value, fixedStyle: m.value === 'fixed' ? (pick.fixedStyle ?? styles[0]!.slug) : null })}
                      className={cn('rounded-md border p-3 text-left', pick.mode === m.value ? 'border-primary bg-primary/10' : 'border-border')}
                    >
                      <span className="block text-sm font-medium">{m.label}</span>
                      <span className="block text-xs text-muted-foreground">{m.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {pick.mode === 'fixed' ? (
                <label className="flex items-center gap-2 text-sm">
                  Style
                  <select
                    className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                    value={pick.fixedStyle ?? ''}
                    onChange={(e) => onChange({ ...pick, fixedStyle: e.target.value })}
                  >
                    {styles.map((s) => (
                      <option key={s.slug} value={s.slug}>{s.name}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">Styles to use</p>
                  <div className="flex flex-wrap gap-2">
                    {styles.map((s) => (
                      <label key={s.slug} className="flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm">
                        <input type="checkbox" checked={dipakai(s.slug)} onChange={() => toggleStyle(s.slug)} />
                        {s.name}
                      </label>
                    ))}
                  </div>
                  {!semua && pick.styles.length === 0 && <p className="text-xs text-destructive">Pick at least one style.</p>}
                </div>
              )}
            </>
          )}
          {children}
        </>
      )}
    </Card>
  )
}
